import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { getQueryStringParameters } from '../helpers/urls';
import { ifDefined } from 'lit/directives/if-defined';
import { isDefined } from '../helpers/utils';
import urlInfo from '../helpers/state/url-info';

@customElement('login-page')
export class LoginPage extends LitElement {
    static amplitude_callsite: string = 'Login Page';

    @state()
    isAppSumo: boolean = false;

    @state()
    email?: string;

    @state()
    errorMessage?: string;

    @state()
    isCheckout: boolean = false;

    @state()
    skip_if_already_logged_in: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindUrlState = new StateController(this, urlInfo);

    firstUpdated() {
        // look at url params to see if appsumo = true and email is present
        const queryString = getQueryStringParameters(window.location.hash);
        const source = queryString.get('source');
        const email = queryString.get('email');
        if (source === 'appsumo' && email) {
            this.isAppSumo = true;
        }
        if (source === 'checkout') {
            this.isCheckout = true;
        }
        this.email = email;
        this.errorMessage = queryString.get('message');
        if (queryString.get('skip_if_already_logged_in') === 'true') {
            this.skip_if_already_logged_in = true;
            // if user is already logged in, redirect to callback_url or home page
            if (accountInfo.isAuthenticated) {
                const callback_url = queryString.get('callback_url');
                if (isDefined(callback_url)) {
                    window.location.replace(callback_url);
                } else {
                    window.location.replace('/');
                }
            }
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    setHostBackground() {
        const loginPage = document.querySelector('main-page')?.shadowRoot?.querySelector('login-page') as HTMLElement;
        if (!isDefined(urlInfo.urlOrganization)) {
            loginPage.style.background = 'url(/images/login-background.webp) center/cover no-repeat';
        } else {
            // very light gray
            const backgroundColor = '#f5f5f5';
            loginPage.style.background = backgroundColor;
        }
    }

    render() {
        this.setHostBackground();
        return html`
            ${urlInfo.urlOrganization ? html`${this.renderOrganizationLogo()}` : ''}
            <div class="signinContainer">
                <signin-modal-component
                    .isAppSumo=${this.isAppSumo}
                    .errorMessage=${this.errorMessage}
                    .isCheckout=${this.isCheckout}
                    emailAddress=${ifDefined(this.email)}>
                </signin-modal-component>
            </div>
            ${this.renderPoweredBy()}
            <error-toast id="errorToast"></error-toast>
        `;
    }

    renderOrganizationLogo() {
        if (urlInfo.urlOrganization) {
            return html`
                <div class="organizationLogo">
                    <img src=${urlInfo.urlOrganization.logo} alt="Workspace Logo" />
                </div>
            `;
        }
    }

    renderPoweredBy() {
        return html`
            <div class="poweredBy" style="text-align: center;" @click=${() => window.open('https://proxi.co')}>
                <i>Powered by Proxi Maps</i>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                min-height: 100vh;
                box-sizing: border-box;
                padding: 2rem 1.5rem;
                background-position: center;
                background-repeat: no-repeat;
                background-size: cover;
            }

            .organizationLogo {
                display: flex;
                justify-content: center;
                margin-bottom: 1rem;
                margin-top: 1rem;
            }

            .poweredBy {
                font-size: 0.75rem;
                cursor: pointer;
                margin-bottom: 1rem;
                color: var(--main-moon-color);
                margin-top: 1rem;
            }

            .organizationLogo img {
                max-width: 200px;
                max-height: 50px;
            }

            .signinContainer {
                max-width: 500px;
                width: 100%;
                padding: 0.5rem;
                border-radius: 0.5rem;
                background: var(--theme-white-color);
                box-shadow: 0px 2px 2px -2px rgba(31, 27, 45, 0.08),
                    0px 4px 12px rgba(31, 27, 45, 0.08);
            }

            @media only screen and (max-width: 550px) {
                :host {
                    background: var(--theme-white-color);
                    padding: 1rem;
                }

                .signinContainer {
                    box-shadow: none;
                }
            }
        `;
    }
}
